import { PLAQUES } from "../content/plaques";
import type { PlaqueUnlockEvent } from "./plaques";
import type { RankStatus } from "./rank";
import type { StreakEvent } from "./streak";

export type RewardKind = "clarity" | "pb" | "plaque" | "rank" | "streak" | "ring" | "mastery" | "daily-card" | "secret";

export type Reward = {
  kind: RewardKind;
  tier: 0 | 1 | 2 | 3 | 4;
  title: string;
  detail?: string;
  value?: number;
  meta?: unknown;
};

export function clarityReward(amount: number): Reward {
  return { kind: "clarity", tier: 0, title: `+${amount} Clarity`, value: amount };
}

export function pbReward(title: string, value: number): Reward {
  return { kind: "pb", tier: 2, title, value };
}

export function masteryReward(title: string): Reward {
  return { kind: "mastery", tier: 1, title };
}

/** Not every streak event is celebration-worthy — a consumed rest day or a
    break is informational (shown as a calm banner), not a reward. */
export function rewardFromStreakEvent(ev: StreakEvent): Reward | null {
  switch (ev.type) {
    case "streak":
      return { kind: "streak", tier: 0, title: `${ev.days}-day streak`, value: ev.days };
    case "rest-day-earned":
      return { kind: "streak", tier: 1, title: "Rest day earned" };
    case "streak-repaired":
      return { kind: "streak", tier: 2, title: "Streak restored", detail: `${ev.days} days`, value: ev.days };
    case "ring-closed":
      return { kind: "ring", tier: 1, title: "Ring closed" };
    case "rest-day-consumed":
    case "streak-broken":
      return null;
  }
}

export function rewardFromPlaque(ev: PlaqueUnlockEvent): Reward {
  const plaque = PLAQUES.find((p) => p.id === ev.id)!;
  return {
    kind: plaque.class === "secret" ? "secret" : "plaque",
    tier: plaque.class === "secret" ? 4 : 2,
    title: plaque.name,
    detail: plaque.how,
    meta: { id: plaque.id, class: plaque.class },
  };
}

export function rewardFromRankChange(before: RankStatus, after: RankStatus): Reward | null {
  if (before.tier === after.tier && before.prestige === after.prestige) return null;
  return { kind: "rank", tier: 3, title: after.name, meta: { tier: after.tier, prestige: after.prestige } };
}

/** At most one T3+ celebration takeover per session — pick the single
    highest-tier reward to headline; everything else (even another T3+)
    gets deferred to a compact row on the Session Card instead. */
export function pickHeadlineReward(rewards: Reward[]): Reward | null {
  const big = rewards.filter((r) => r.tier >= 3);
  if (big.length === 0) return null;
  return big.reduce((best, r) => (r.tier > best.tier ? r : best), big[0]!);
}

export function deferredRewards(rewards: Reward[], headline: Reward | null): Reward[] {
  if (!headline) return rewards;
  let skipped = false;
  return rewards.filter((r) => {
    if (!skipped && r === headline) {
      skipped = true;
      return false;
    }
    return true;
  });
}
